import React, {useEffect, useState} from 'react';
import {Button, Dropdown, Menu} from 'antd';
import type { MenuProps } from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {BookOutlined, CodeOutlined, FolderOpenOutlined, HomeOutlined, InfoCircleOutlined, MenuOutlined, PictureOutlined, ToolOutlined, EllipsisOutlined, ThunderboltOutlined, LockOutlined, SearchOutlined, ShareAltOutlined} from '@ant-design/icons';
import {ROUTE_TRANSLATION_KEYS, ROUTES} from '../../routes/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import { buildImgCloudRoutePath } from '../../utils/appRouting';
import './Navigation.css';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [enableImageSearch, setEnableImageSearch] = useState<boolean>(false);
  const htmlShareCollectionKey = 'html-share-collection';
  const selectedPath = location.pathname === '/index.html' || location.pathname === '/img-cloud/index.html'
    ? ROUTES.HOME
    : location.pathname;

  const handleInternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    navigate(path);
  };

  const renderInternalLink = (path: string, label: React.ReactNode) => (
    <a
      className="navigation-link"
      href={buildImgCloudRoutePath(path)}
      onClick={(event) => handleInternalLinkClick(event, path)}
    >
      {label}
    </a>
  );

  const renderExternalLink = (url: string, label: React.ReactNode) => (
    <a className="navigation-link" href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );

  // 内部路由菜单项
  const internalMenuItems = [
    {
      key: ROUTES.HOME,
      icon: <HomeOutlined />,
      label: renderInternalLink(ROUTES.HOME, t(ROUTE_TRANSLATION_KEYS[ROUTES.HOME])),
    },
    {
      key: ROUTES.WEB_HOSTING,
      icon: <CodeOutlined />,
      label: renderInternalLink(ROUTES.WEB_HOSTING, t(ROUTE_TRANSLATION_KEYS[ROUTES.WEB_HOSTING])),
    },
    {
      key: ROUTES.GALLERIES,
      icon: <PictureOutlined />,
      label: renderInternalLink(ROUTES.GALLERIES, t(ROUTE_TRANSLATION_KEYS[ROUTES.GALLERIES])),
    },
    {
      key: ROUTES.IMAGE_PROCESSING,
      icon: <ToolOutlined />,
      label: renderInternalLink(ROUTES.IMAGE_PROCESSING, t(ROUTE_TRANSLATION_KEYS[ROUTES.IMAGE_PROCESSING])),
    },
  ];

  // 获取用户配置
  useEffect(() => {
    fetch('/login/info', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((e) => {
        if (e.isSuccess && e.detail) {
          try {
            const config = typeof e.detail.config === 'string' 
              ? JSON.parse(e.detail.config || '{}') 
              : e.detail.config || {};
            setEnableImageSearch(config.enableImageSearch === true);
          } catch (err) {
            console.error('解析用户配置失败:', err);
            setEnableImageSearch(false);
          }
        }
      })
      .catch((err) => {
        console.error('获取用户信息失败:', err);
        setEnableImageSearch(false);
      });
  }, []);

  // 更多菜单项（博客、关于）- 英文站不显示博客
  const moreMenuItems = [
    // 只有启用图搜功能的用户才能看到图搜入口
    ...(enableImageSearch ? [{
      key: ROUTES.IMAGE_SEARCH,
      icon: <SearchOutlined />,
      label: renderInternalLink(ROUTES.IMAGE_SEARCH, t(ROUTE_TRANSLATION_KEYS[ROUTES.IMAGE_SEARCH])),
    }] : []),
    // 英文站隐藏 AI 生图入口
    ...(!isEnglishSite() ? [{
      key: ROUTES.AI_GENERATE,
      icon: <ThunderboltOutlined />,
      label: renderInternalLink(ROUTES.AI_GENERATE, t(ROUTE_TRANSLATION_KEYS[ROUTES.AI_GENERATE])),
    }] : []),
    {
      key: htmlShareCollectionKey,
      icon: <ShareAltOutlined />,
      label: renderExternalLink('/html-share', t('routes.htmlShareCollection')),
    },
    {
      key: ROUTES.ENCRYPTED_LINKS,
      icon: <LockOutlined />,
      label: renderInternalLink(ROUTES.ENCRYPTED_LINKS, t(ROUTE_TRANSLATION_KEYS[ROUTES.ENCRYPTED_LINKS])),
    },
    {
      key: ROUTES.DIRECTORIES,
      icon: <FolderOpenOutlined />,
      label: renderInternalLink(ROUTES.DIRECTORIES, t(ROUTE_TRANSLATION_KEYS[ROUTES.DIRECTORIES])),
    },
    // 英文站不显示博客入口
    ...(!isEnglishSite() ? [{
      key: 'external-docs',
      icon: <BookOutlined />,
      label: renderExternalLink('https://blog.imgbed.cn/', t('navigation.blog')),
    }] : []),
    // 英文站不展示 About 菜单
    ...(!isEnglishSite() ? [{
      key: ROUTES.ABOUT,
      icon: <InfoCircleOutlined />,
      label: renderInternalLink(ROUTES.ABOUT, t(ROUTE_TRANSLATION_KEYS[ROUTES.ABOUT])),
    }] : []),
  ];

  // 合并所有菜单项
  const menuItems = [
    ...internalMenuItems,
    {
      key: 'more',
      icon: <EllipsisOutlined />,
      label: t('navigation.more'),
      children: moreMenuItems,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    const target = domEvent.target as HTMLElement | null;
    if (target?.closest('a')) {
      return;
    }

    // 处理外部博客链接
    if (key === 'external-docs') {
      window.open('https://blog.imgbed.cn/', '_blank', 'noopener,noreferrer');
      return;
    }
    if (key === htmlShareCollectionKey) {
      window.open('/html-share', '_blank', 'noopener,noreferrer');
      return;
    }
    // 内部路由导航
    navigate(key);
  };

  return (
    <>
      {/* 桌面端导航菜单 */}
      <div className="desktop-navigation">
        <Menu
          mode="horizontal"
          selectedKeys={[selectedPath]}
          items={menuItems}
          onClick={handleMenuClick}
          forceSubMenuRender
          style={{
            border: 'none',
            background: 'transparent',
            flex: 1,
            marginLeft: '0px',
          }}
        />
      </div>

      {/* 移动端下拉菜单 */}
      <div className="mobile-navigation">
        <Dropdown
          menu={{
            items: menuItems,
            selectedKeys: [selectedPath],
            onClick: handleMenuClick,
            forceSubMenuRender: true,
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            style={{
              fontSize: '18px',
              color: '#4C5362',
              border: 'none',
              padding: '8px',
              height: '40px',
              width: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Dropdown>
      </div>
    </>
  );
};

export default Navigation;
